import { useCallback } from "react";
import UserComboBox from "./usercombobox";

export interface PagedComboboxItem {
  id: string;
  name: string;
}

interface PagedResult<T> {
  items: T[];
}

interface PagedComboboxProps<T extends PagedComboboxItem> {
  /** Currently selected item. `null` when nothing is selected. */
  value: T | null;
  onChange: (item: T | null) => void;
  /** Calls the paged endpoint and returns its result. */
  fetchPage: (args: {
    search: string;
    pageNumber: number;
    pageSize: number;
  }) => Promise<PagedResult<T>>;
  /** Singular noun used in the trigger ("Select {title}") and search placeholder. */
  title?: string;
  /** Renders the item label. Defaults to `item.name`. */
  renderLabel?: (item: T) => string;
  pageSize?: number;
  disabled?: boolean;
}

/**
 * Searchable + paginated combobox. Hits the API on every debounced keystroke
 * and supports "Load More" for additional pages. Designed to wrap our paged
 * endpoints (`getXPaged({ pageNumber, pageSize, search })`).
 */
export function PagedCombobox<T extends PagedComboboxItem>({
  value,
  onChange,
  fetchPage,
  title = "item",
  renderLabel = (item) => item.name,
  pageSize = 25,
  disabled = false,
}: PagedComboboxProps<T>) {
  const searchFn = useCallback(
    async (search: string, offset: number, size: number): Promise<T[]> => {
      const pageNumber = Math.floor(offset / size) + 1;
      const result = await fetchPage({ search, pageNumber, pageSize: size });
      return result.items;
    },
    [fetchPage],
  );

  return (
    <UserComboBox<T>
      title={title}
      valueKey="id"
      value={value ?? undefined}
      size={pageSize}
      renderText={renderLabel}
      onChange={(item) => onChange(item)}
      searchFn={searchFn}
      disabled={disabled}
    />
  );
}
